import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { useDispatch } from "react-redux";

import {
  fetchPriceSummary,
} from "../../actions/priceActions";

import {
  useCart,
} from "../../components/CartContext";

import "./css/CheckoutScreen.css";


function CheckoutScreen() {

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const {
    restaurantId,
    tableId,
  } = useParams();


  // =====================================================
  // CART
  // =====================================================

  const {
    cart,
    cartCount,
    cartTotal,
  } = useCart();


  // =====================================================
  // PRICE SUMMARY
  // =====================================================

  const [
    priceSummary,
    setPriceSummary,
  ] = useState({

    subtotal: 0,

    tax: 0,

    serviceCharge: 0,

    total: 0,

  });


  const [
    loading,
    setLoading,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  // =====================================================
  // HOME PATH
  // =====================================================

  const homePath =
    `/${restaurantId}/table/${tableId}`;


  // =====================================================
  // FORMAT PRICE
  // =====================================================

  const formatPrice = (
    value
  ) => {

    return Number(
      value || 0
    ).toFixed(2);

  };


  // =====================================================
  // FETCH PRICE SUMMARY
  // =====================================================

  useEffect(() => {

    const calculatePrice = async () => {

      if (
        !restaurantId ||
        !cart ||
        cart.length === 0
      ) {

        return;

      }


      try {

        setLoading(true);

        setError("");


        const items =
          cart.map(
            (item) => ({

              menu_item_id:
                item.id,

              quantity:
                item.quantity,

            })
          );


        const response =
          await dispatch(
            fetchPriceSummary(
              restaurantId,
              items
            )
          );


        setPriceSummary({

          subtotal:
            Number(
              response?.subtotal || 0
            ),

          tax:
            Number(
              response?.tax || 0
            ),

          serviceCharge:
            Number(
              response?.service_charge || 0
            ),

          total:
            Number(
              response?.total || 0
            ),

        });

      } catch (err) {

        console.error(
          "Price summary error:",
          err
        );


        setError(
          err?.response?.data?.detail ||
          err?.message ||
          "Unable to calculate your order total."
        );

      } finally {

        setLoading(false);

      }

    };


    calculatePrice();

  }, [
    dispatch,
    restaurantId,
    cart,
  ]);


  // =====================================================
  // BACK TO CART
  // =====================================================

  const handleBackToCart = () => {

    navigate(
      `${homePath}/cart`
    );

  };


  // =====================================================
  // CONTINUE TO ORDER
  // =====================================================

  const handleContinue = () => {

    if (
      !cart ||
      cart.length === 0
    ) {

      navigate(homePath);

      return;

    }


    navigate(
      `${homePath}/order`,
      {
        state: {

          subtotal:
            Number(
              priceSummary.subtotal
            ),

          tax:
            Number(
              priceSummary.tax
            ),

          serviceCharge:
            Number(
              priceSummary.serviceCharge
            ),

          total:
            Number(
              priceSummary.total
            ),

        },
      }
    );

  };


  // =====================================================
  // EMPTY CART
  // =====================================================

  if (
    !cart ||
    cart.length === 0
  ) {

    return (

      <main className="t7-checkout-page">

        <div className="t7-checkout-empty">

          <div className="t7-checkout-empty-icon">
            🛒
          </div>


          <span className="t7-checkout-label">
            CHECKOUT
          </span>


          <h1>
            Your cart is empty
          </h1>


          <p>
            Add some delicious dishes
            before continuing.
          </p>


          <button
            type="button"
            className="t7-checkout-orange-button"
            onClick={() =>
              navigate(homePath)
            }
          >

            Explore Menu

            <span>
              →
            </span>

          </button>

        </div>

      </main>

    );

  }


  return (

    <main className="t7-checkout-page">


      {/* =================================================
          HEADER
      ================================================= */}

      <section className="t7-checkout-header">

        <div className="t7-checkout-container">


          <button
            type="button"
            className="t7-checkout-back"
            onClick={
              handleBackToCart
            }
          >

            <span>
              ←
            </span>

            Back to Cart

          </button>


          <div className="t7-checkout-title-row">


            <div>

              <span className="t7-checkout-label">
                ALMOST THERE
              </span>


              <h1>
                Checkout
              </h1>

            </div>


            <div className="t7-checkout-table">

              <span>
                TABLE
              </span>

              <strong>
                {tableId}
              </strong>

            </div>

          </div>

        </div>

      </section>



      {/* =================================================
          MAIN CONTENT
      ================================================= */}

      <section className="t7-checkout-content">

        <div className="t7-checkout-container">


          <div className="t7-checkout-layout">


            {/* =================================================
                LEFT
            ================================================= */}

            <div className="t7-checkout-order">


              {/* =================================================
                  ORDER ITEMS
              ================================================= */}

              <div className="t7-checkout-section-head">

                <div>

                  <span>
                    01
                  </span>

                  <h2>
                    Review your order
                  </h2>

                </div>

              </div>


              <div className="t7-checkout-items">

                {cart.map(
                  (item) => (

                    <article
                      className="t7-checkout-item"
                      key={item.id}
                    >


                      <div className="t7-checkout-item-image">

                        {item.image ? (

                          <img
                            src={item.image}
                            alt={item.name}
                          />

                        ) : (

                          <div className="t7-checkout-placeholder">
                            ✦
                          </div>

                        )}

                      </div>


                      <div className="t7-checkout-item-info">

                        <span>
                          {item.categoryName ||
                            "SPICE & SAFFRON"}
                        </span>


                        <h3>
                          {item.name}
                        </h3>


                        <p>
                          ₹
                          {formatPrice(
                            item.price
                          )}
                        </p>

                      </div>


                      <div className="t7-checkout-item-quantity">

                        <span>
                          Qty
                        </span>

                        <strong>
                          {item.quantity}
                        </strong>

                      </div>


                      <div className="t7-checkout-item-total">

                        ₹
                        {formatPrice(
                          Number(item.price) *
                          Number(item.quantity)
                        )}

                      </div>

                    </article>

                  )
                )}

              </div>


              {/* =================================================
                  TABLE INFORMATION
              ================================================= */}

              <div className="t7-table-card">

                <div className="t7-table-icon">
                  {tableId}
                </div>


                <div>

                  <span>
                    DINING TABLE
                  </span>

                  <h3>
                    Table {tableId}
                  </h3>

                  <p>
                    Your order will be
                    prepared for this table.
                  </p>

                </div>

              </div>


              {/* =================================================
                  INFORMATION
              ================================================= */}

              <div className="t7-checkout-info">

                <span>
                  ✦
                </span>

                <p>
                  Please review your items
                  and order total before
                  continuing.
                </p>

              </div>

            </div>



            {/* =================================================
                RIGHT SUMMARY
            ================================================= */}

            <aside className="t7-checkout-summary">


              <div className="t7-summary-top">

                <span>
                  02
                </span>

                <span>
                  ORDER TOTAL
                </span>

              </div>


              <h2>
                Your order
              </h2>


              {/* =================================================
                  ITEM COUNT
              ================================================= */}

              <div className="t7-checkout-count">

                <span>
                  {cartCount}
                  {" "}
                  {cartCount === 1
                    ? "item"
                    : "items"}
                </span>

                <strong>
                  ₹
                  {formatPrice(
                    cartTotal
                  )}
                </strong>

              </div>


              <div className="t7-checkout-divider"></div>


              {/* =================================================
                  SUBTOTAL
              ================================================= */}

              <div className="t7-checkout-price-row">

                <span>
                  Subtotal
                </span>

                <strong>
                  ₹
                  {formatPrice(
                    priceSummary.subtotal
                  )}
                </strong>

              </div>


              {/* =================================================
                  TAX
              ================================================= */}

              <div className="t7-checkout-price-row">

                <span>
                  Tax
                </span>

                <strong>
                  ₹
                  {formatPrice(
                    priceSummary.tax
                  )}
                </strong>

              </div>


              {/* =================================================
                  SERVICE CHARGE
              ================================================= */}

              <div className="t7-checkout-price-row">

                <span>
                  Service Charge
                </span>

                <strong>
                  ₹
                  {formatPrice(
                    priceSummary.serviceCharge
                  )}
                </strong>

              </div>


              <div className="t7-checkout-divider"></div>


              {/* =================================================
                  TOTAL
              ================================================= */}

              <div className="t7-checkout-total">

                <span>
                  TOTAL
                </span>

                <strong>
                  ₹
                  {formatPrice(
                    priceSummary.total
                  )}
                </strong>

              </div>


              {/* =================================================
                  ERROR
              ================================================= */}

              {error && (

                <div className="t7-checkout-error">

                  <strong>
                    Unable to calculate total
                  </strong>

                  <p>
                    {error}
                  </p>

                </div>

              )}


              {/* =================================================
                  CONTINUE
              ================================================= */}

              <button
                type="button"
                className="t7-checkout-continue"
                onClick={
                  handleContinue
                }
                disabled={
                  loading ||
                  Boolean(error)
                }
              >

                {loading ? (

                  <>
                    Calculating...
                  </>

                ) : (

                  <>
                    Continue to Order

                    <span>
                      →
                    </span>
                  </>

                )}

              </button>


              {/* =================================================
                  PAYMENT NOTE
              ================================================= */}

              <div className="t7-checkout-secure">

                <span>
                  🔒
                </span>

                <p>
                  Payment method will be
                  selected on the next step.
                </p>

              </div>

            </aside>

          </div>

        </div>

      </section>



      {/* =================================================
          FOOTER NOTE
      ================================================= */}

      <section className="t7-checkout-footer-note">

        <div className="t7-checkout-container">

          <span>
            ✦
          </span>

          <p>
            Authentic Indian flavours,
            freshly prepared for your table.
          </p>

        </div>

      </section>

    </main>

  );

}


export default CheckoutScreen;